import type { BriefJob, CampaignDraft, NarrativeAngle } from "@/lib/campaign-draft";
import type { Brand, Persona, Product } from "@shared/schema";

export interface CreativeBriefPayload {
  product: unknown;
  persona: unknown;
  brand: unknown;
  funnel_stage: unknown;
  ad_unit: unknown;
  angle: unknown;
  campaign_context: unknown;
  /**
   * The user-picked reference image for this ad unit at this stage. Sent at
   * the top level (in addition to `ad_unit.reference_image_url`) so the Sim
   * workflow can use it as the visual base when crafting the brief / prompt.
   */
  reference_image_url: string | null;
}

function serializeAngle(angle: NarrativeAngle | null | undefined) {
  if (!angle) return null;
  return {
    id: angle.id,
    name: angle.name,
    family: angle.family,
    subtype: angle.subtype,
    summary: angle.summary,
    core_tension: angle.coreTension,
    persona_relevance: angle.personaRelevance,
    product_truth: angle.productTruth,
    belief_shift: angle.beliefShift,
    promise: angle.promise,
    resonance_score: angle.resonanceScore,
  };
}

function serializeBrand(brand: Brand) {
  return {
    id: brand.id,
    name: brand.name,
    category: brand.category ?? null,
    summary: brand.summary ?? null,
    founder_story: brand.founderStory ?? null,
    mission: brand.mission ?? null,
    positioning: brand.positioning ?? null,
    worldview: brand.worldview ?? null,
    enemy: brand.enemy ?? null,
    promise: brand.promise ?? null,
    tone_summary: brand.toneSummary ?? null,
    proof_hierarchy: brand.proofHierarchy ?? null,
    approved_phrases: brand.approvedPhrases ?? null,
    banned_phrases: brand.bannedPhrases ?? null,
    visual_rules: brand.visualRules ?? null,
    claim_guardrails: brand.claimGuardrails ?? null,
    strategic_priorities: brand.strategicPriorities ?? null,
  };
}

function serializePersona(persona: Persona) {
  return {
    id: persona.id,
    name: persona.name,
    awareness_stage: persona.awarenessStage ?? null,
    summary: persona.summary ?? null,
    worldview: persona.worldview ?? null,
    primary_pain: persona.primaryPain ?? null,
    desired_outcome: persona.desiredOutcome ?? null,
    deep_fear: persona.deepFear ?? null,
    core_beliefs: persona.coreBeliefsJson ?? null,
    objections: persona.objectionsJson ?? null,
    trust_triggers: persona.trustTriggersJson ?? null,
    purchase_drivers: persona.purchaseDriversJson ?? null,
    emotional_hooks: persona.emotionalHooksJson ?? null,
    language_patterns: persona.languagePatternsJson ?? null,
    customer_voice_quotes: persona.customerVoiceQuotesJson ?? null,
    secondary_pains: persona.secondaryPainsJson ?? null,
    old_solution: persona.oldSolution ?? null,
    current_frustration: persona.currentFrustration ?? null,
    proof_required: persona.proofRequired ?? null,
    preferred_tone: persona.preferredTone ?? null,
    preferred_channels: persona.preferredChannels ?? null,
    visual_cues: persona.visualCues ?? null,
  };
}

function serializeProduct(product: Product) {
  return {
    id: product.id,
    name: product.name,
    category: product.category ?? null,
    product_role: product.productRole ?? null,
    summary: product.summary ?? null,
    description: product.description ?? null,
    job_to_be_done: product.jobToBeDone ?? null,
    ingredients_or_features: product.ingredientsOrFeatures ?? null,
    use_cases: product.useCases ?? null,
    objections: product.objections ?? null,
    proof_points: product.proofPoints ?? null,
    price_text: product.priceText ?? null,
    subscription_available: product.subscriptionAvailable ?? false,
    is_bundle: product.isBundle ?? false,
    claim_notes: product.claimNotes ?? null,
  };
}

export function buildCreativeBriefPayload(
  job: BriefJob,
  draft: CampaignDraft,
  brand: Brand | null
): CreativeBriefPayload {
  const { product, persona, funnelStrategy, narrativeAngles, campaignPlan } = draft;

  const stageStrategy =
    funnelStrategy?.stages.find(
      (s) =>
        s.stage.toLowerCase() === job.stage.toLowerCase() ||
        (s.stage_group && s.stage_group.toLowerCase() === job.funnelStageId.toLowerCase())
    ) ?? null;

  const angle =
    (job.angleId ? narrativeAngles.find((a) => a.id === job.angleId) : null) ??
    narrativeAngles[job.instanceIndex % Math.max(narrativeAngles.length, 1)] ??
    null;

  const stageStrategyEntry = campaignPlan?.stageStrategy?.find(
    (s) => s.stage.toLowerCase() === job.stage.toLowerCase()
  ) ?? null;

  const campaignContext = {
    executive_summary: campaignPlan?.executiveSummary ?? null,
    stage_pitch: stageStrategyEntry?.pitch ?? null,
    stage_psychological_goal: stageStrategyEntry?.psychologicalGoal ?? null,
    messaging_strategy: stageStrategyEntry?.messagingStrategy ?? null,
    copy_directions: stageStrategyEntry?.copyDirections ?? [],
    why_this_campaign_wins: campaignPlan?.whyThisCampaignWins ?? [],
  };

  return {
    product: product ? serializeProduct(product as Product) : null,
    persona: persona ? serializePersona(persona as Persona) : null,
    brand: brand ? serializeBrand(brand) : null,
    funnel_stage: stageStrategy
      ? {
          stage: stageStrategy.stage,
          objective: stageStrategy.objective,
          psychological_task: stageStrategy.psychological_task,
          messaging_focus: stageStrategy.messaging_focus,
          emotional_job: stageStrategy.emotional_job,
          proof_focus: stageStrategy.proof_focus,
          message_style: stageStrategy.message_style,
          transition_to_next_stage: stageStrategy.transition_to_next_stage,
        }
      : { stage: job.stage },
    ad_unit: {
      id: job.adUnit.id,
      name: job.adUnit.name,
      format: job.adUnit.format,
      category: job.adUnit.category,
      description: job.adUnit.description,
      supported_funnel_stages: job.adUnit.supported_funnel_stages,
      recommended_use_case: job.adUnit.recommended_use_case,
      reference_image_url: job.referenceImageUrl ?? null,
    },
    angle: serializeAngle(angle),
    campaign_context: campaignContext,
    reference_image_url: job.referenceImageUrl ?? null,
  };
}
